import { useMemo, useState } from 'react';
import { SectionCard } from '@/components/DataList';
import { Icon } from '@/components/icons';
import { auth, wallet } from '@/api/endpoints';
import { errorMessage, pickList, type JsonMap } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import type { SessionUser } from '@/auth/session';
import {
  Button,
  Empty,
  ErrorBox,
  Loading,
  Money,
} from '@/components/ui';
import { required, str } from '@/utils/format';

type WalletTab = 'recharge' | 'games';

export function WalletScreen() {
  const [tab, setTab] = useState<WalletTab>('recharge');
  const [methodId, setMethodId] = useState('');
  const [amount, setAmount] = useState('');
  const [refNo, setRefNo] = useState('');
  const [senderId, setSenderId] = useState('');
  const [note, setNote] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const state = useAsync(
    async () => {
      const [meRes, methodsRes, txRes] = await Promise.all([
        auth.me(),
        wallet.paymentMethods(tab),
        wallet.transactions({ wallet: tab }),
      ]);
      if (!meRes.success) return meRes;
      const txPayload = txRes as JsonMap;
      return {
        success: true,
        user: (meRes.user && typeof meRes.user === 'object' ? meRes.user : {}) as SessionUser,
        methods: methodsRes.success ? pickList(methodsRes, 'methods', 'items', 'data') : [],
        transactions: txRes.success ? pickList(txRes, 'transactions', 'items', 'data') : [],
        summary:
          txPayload.summary && typeof txPayload.summary === 'object'
            ? (txPayload.summary as JsonMap)
            : ({ total_purchase: '0', total_recharge: '0' } as JsonMap),
      };
    },
    (p) => ({
      user: (p.user && typeof p.user === 'object' ? p.user : {}) as SessionUser,
      methods: Array.isArray(p.methods) ? (p.methods as JsonMap[]) : pickList(p, 'methods'),
      transactions: Array.isArray(p.transactions)
        ? (p.transactions as JsonMap[])
        : pickList(p, 'transactions'),
      summary:
        p.summary && typeof p.summary === 'object'
          ? (p.summary as JsonMap)
          : ({ total_purchase: '0', total_recharge: '0' } as JsonMap),
    }),
    [tab],
  );

  const methods = state.data?.methods ?? [];
  const selectedMethod = useMemo(
    () => methods.find((m) => String(m.id) === methodId) ?? methods[0] ?? null,
    [methods, methodId],
  );

  async function requestDeposit() {
    const miss = required({ amount }, ['amount']);
    if (miss) {
      setMsg(miss);
      return;
    }
    if (!selectedMethod) {
      setMsg('اختر طريقة الدفع.');
      return;
    }
    setBusy(true);
    setMsg('');
    const res = await wallet.depositRequest({
      payment_method_id: selectedMethod.id,
      amount,
      tx_reference: refNo,
      sender_identifier: senderId,
      note,
    });
    setBusy(false);
    if (!res.success) {
      setMsg(errorMessage(res));
      return;
    }
    setMsg(typeof res.message === 'string' ? res.message : 'تم إرسال طلب الشحن.');
    setAmount('');
    setRefNo('');
    setSenderId('');
    setNote('');
    state.reload();
  }

  if (state.loading) return <Loading />;
  if (state.error) return <ErrorBox message={state.error} onRetry={state.reload} />;

  const user = state.data?.user;
  const summary = state.data?.summary ?? {};
  const txs = state.data?.transactions ?? [];

  return (
    <div className="stack-gap">
      <section className="grid-4 wallet-overview">
        <article className="stat-card">
          <Icon name="wallet" size={22} />
          <span>محفظة الوحدات</span>
          <strong>
            <Money value={user?.wallet_balance} />
          </strong>
        </article>
        <article className="stat-card">
          <Icon name="game" size={22} />
          <span>محفظة الألعاب</span>
          <strong>
            <Money value={user?.game_wallet_balance ?? user?.games_wallet_balance ?? 0} suffix="$" />
          </strong>
        </article>
        <article className="stat-card">
          <Icon name="deposit" size={22} />
          <span>إجمالي الشراء</span>
          <strong>
            <Money
              value={summary.total_purchase}
              suffix={tab === 'games' ? '$' : 'ل.س'}
            />
          </strong>
        </article>
        <article className="stat-card">
          <Icon name="zap" size={22} />
          <span>إجمالي الشحن</span>
          <strong>
            <Money
              value={summary.total_recharge}
              suffix={tab === 'games' ? '$' : 'ل.س'}
            />
          </strong>
        </article>
      </section>

      <div className="tabs-row">
        <button
          type="button"
          className={tab === 'recharge' ? 'active' : ''}
          onClick={() => {
            setTab('recharge');
            setMethodId('');
          }}
        >
          شحن محفظة الوحدات
        </button>
        <button
          type="button"
          className={tab === 'games' ? 'active' : ''}
          onClick={() => {
            setTab('games');
            setMethodId('');
          }}
        >
          شحن محفظة الألعاب
        </button>
      </div>

      <SectionCard
        title={tab === 'games' ? 'شحن محفظة الألعاب بالدولار' : 'شحن محفظة الوحدات بالليرة'}
        subtitle="اختر طريقة الدفع، حوّل المبلغ، ثم أرسل رقم العملية للمراجعة."
        icon={<Icon name="wallet" size={24} />}
      >
        {methods.length === 0 ? (
          <Empty title="لا توجد طرق دفع" hint="الإدارة لم تفعّل طرق دفع لهذه المحفظة بعد." />
        ) : (
          <div className="form-grid two-columns">
            <label>
              <span>طريقة الدفع</span>
              <select
                value={methodId || str(selectedMethod?.id)}
                onChange={(e) => setMethodId(e.target.value)}
              >
                {methods.map((m) => (
                  <option key={str(m.id)} value={str(m.id)}>
                    {str(m.name)} — {str(m.currency, 'SYP')}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>المبلغ</span>
              <input
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="decimal"
                placeholder={tab === 'games' ? 'مثال: 10' : 'مثال: 50000'}
              />
            </label>
            <label>
              <span>{str(selectedMethod?.account_label, 'الحساب')}</span>
              <input value={str(selectedMethod?.account_value)} readOnly />
            </label>
            <label>
              <span>رقم العملية / التحويل</span>
              <input
                value={refNo}
                onChange={(e) => setRefNo(e.target.value)}
                placeholder="اختياري لكنه مفضل"
              />
            </label>
            <label>
              <span>معرّف المرسل</span>
              <input
                value={senderId}
                onChange={(e) => setSenderId(e.target.value)}
                placeholder="اختياري"
              />
            </label>
            <label>
              <span>ملاحظة</span>
              <input value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
            {msg ? <p className={msg.includes('تم') ? 'ok' : 'error'}>{msg}</p> : null}
            <Button type="button" className="big-action" disabled={busy} onClick={() => void requestDeposit()}>
              {busy ? 'جاري الإرسال…' : 'إرسال طلب الشحن'}
            </Button>
          </div>
        )}
      </SectionCard>

      <SectionCard title="آخر الحركات" icon={<Icon name="orders" size={24} />}>
        {txs.length === 0 ? (
          <Empty title="لا توجد حركات" />
        ) : (
          <div className="list-stack">
            {txs.slice(0, 40).map((tx, i) => (
              <article key={str(tx.id, String(i))} className="list-card">
                <div className="list-row">
                  <div className="cell-stack">
                    <strong>{str(tx.description ?? tx.type ?? tx.note)}</strong>
                    <small className="muted">{str(tx.created_at)}</small>
                  </div>
                  <strong>
                    <Money value={tx.amount ?? tx.value} suffix={str(tx.currency) === 'USD' ? '$' : 'ل.س'} />
                  </strong>
                </div>
              </article>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
